import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import * as Sentry from '@sentry/vue';
import {
  getEditStatus,
  getEditView,
  isEditView,
  quoteEdit,
  readEditError,
  requestEditLink,
  submitEdit,
} from '@/api/orderEdit';
import type { EditApiError } from '@/api/orderEdit';
import { RULE_CODES } from '@/api/types/orderEdit';
import type {
  EditCredential,
  EditErrorCode,
  EditLine,
  EditView,
  ModificationStatus,
  Quote,
  SubmitResult,
  Target,
} from '@/api/types/orderEdit';
import { clampTarget, firstQuantity, isBrunch, isLineEditable, maxFor } from '@/services/orderEditRules';

/** The quote follows the last change by this long (spec §9.1). */
export const QUOTE_DEBOUNCE_MS = 500;
/** Back from Stripe: ask for the status this often, for this long (spec §9.1). */
export const STATUS_POLL_INTERVAL_MS = 4_000;
export const STATUS_POLL_TIMEOUT_MS = 60_000;

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';
export type QuoteState = 'idle' | 'pending' | 'ready' | 'error';
export type SubmitState = 'idle' | 'submitting' | 'redirecting';
export type LinkState = 'idle' | 'sending' | 'sent' | 'error';
export type EditOutcome =
  | 'applied'
  | 'processing'
  | 'paid_pending'
  | 'needs_attention'
  | 'modification_pending'
  | 'payment_in_progress';

export interface RuleError {
  code: EditErrorCode;
  product: string | null;
}

export interface PendingChange {
  product: string;
  label: string;
  from: number;
  to: number;
}

export interface PollOptions {
  intervalMs?: number;
  timeoutMs?: number;
}

const RULE_ERRORS: ReadonlySet<EditErrorCode> = new Set<EditErrorCode>([...RULE_CODES, 'price_unavailable']);
/** The booking left the editable set while the page was open. */
const GONE: ReadonlySet<EditErrorCode> = new Set<EditErrorCode>(['invalid_link', 'disabled', 'not_eligible']);

function credentialValue(cred: EditCredential): string {
  return 'token' in cred ? cred.token : cred.menuToken;
}

function sameCredential(a: EditCredential | null, b: EditCredential): boolean {
  return a !== null && ('token' in a) === ('token' in b) && credentialValue(a) === credentialValue(b);
}

/** Worth a Sentry report: the server or the contract broke (not a customer state, not the network). */
function isUnexpected(err: EditApiError): boolean {
  return err.code === 'unknown' || (err.status !== null && err.status >= 500 && err.code !== 'order_edit_unavailable');
}

/**
 * EAUG-77 order modification, in memory only (the page and the dish-page
 * block never persist anything). One credential per page load: the edit
 * token (edit page, `full`) or the menu link's id (dish page, `drinks_add`).
 */
export const useOrderEditStore = defineStore('orderEdit', () => {
  // The credential lives in this closure, never in a ref: the Sentry Pinia
  // plugin (main.ts) attaches every store's state to every error report.
  let credential: EditCredential | null = null;
  let loadSeq = 0;
  let quoteSeq = 0;
  let quoteTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let pollingId: number | null = null;

  const loadState = ref<LoadState>('idle');
  const loadError = ref<EditErrorCode | null>(null);
  const view = ref<EditView | null>(null);
  const targets = ref<Record<string, number>>({});
  const quote = ref<Quote | null>(null);
  const quoteState = ref<QuoteState>('idle');
  const quoteError = ref<RuleError | null>(null);
  const quoteChanged = ref(false);
  const submitState = ref<SubmitState>('idle');
  const actionError = ref<EditErrorCode | null>(null);
  const deadlinePassed = ref(false);
  const outcome = ref<EditOutcome | null>(null);
  const polling = ref(false);
  const linkState = ref<LinkState>('idle');
  const linkSentTo = ref<string | null>(null);
  const linkError = ref<EditErrorCode | null>(null);

  const lines = computed<EditLine[]>(() => {
    const v = view.value;
    return v ? [...v.sections.menus, ...v.sections.drinks, ...v.sections.options] : [];
  });

  function lineOf(product: string): EditLine | undefined {
    return lines.value.find((l) => l.product === product);
  }

  function labelOf(product: string | null): string {
    return (product && lineOf(product)?.label) || product || '';
  }

  function targetOf(product: string): number {
    return targets.value[product] ?? lineOf(product)?.current ?? 0;
  }

  const pendingChanges = computed<PendingChange[]>(() =>
    lines.value
      .filter((l) => targetOf(l.product) !== l.current)
      .map((l) => ({ product: l.product, label: l.label, from: l.current, to: targetOf(l.product) })),
  );

  const changedTargets = computed<Target[]>(() =>
    pendingChanges.value.map((c) => ({ product: c.product, quantity: c.to })),
  );

  /** Menus added (+) or removed (−) by the draft: H' − H, as the server counts it. */
  const mealsDelta = computed(() =>
    (view.value?.sections.menus ?? []).reduce((sum, l) => sum + targetOf(l.product) - l.current, 0),
  );
  const newHeadcount = computed(() => (view.value?.headcount ?? 0) + mealsDelta.value);
  const mealsBelowHeadcount = computed(() => mealsDelta.value < 0);
  const capacityReached = computed(() => {
    const v = view.value;
    return (
      !!v &&
      v.menusLock === null &&
      v.capacity !== null &&
      v.sections.menus.some(isLineEditable) &&
      newHeadcount.value >= v.capacity
    );
  });

  const canSubmit = computed(
    () =>
      loadState.value === 'ready' &&
      submitState.value === 'idle' &&
      quoteState.value === 'ready' &&
      quote.value !== null &&
      quoteError.value === null &&
      !deadlinePassed.value &&
      pendingChanges.value.length > 0,
  );

  function headroomFor(line: EditLine): number | null {
    const v = view.value;
    if (line.category !== 'meal' || !v || v.capacity === null) return null;
    return v.capacity - newHeadcount.value;
  }

  /** False while the draft's menus are below the headcount that unlocks the line (offeredFrom). */
  function unlockedOf(line: EditLine): boolean {
    return line.offeredFrom === null || newHeadcount.value >= line.offeredFrom;
  }

  function maxOf(line: EditLine): number {
    if (!unlockedOf(line)) return line.current;
    return maxFor(line, targetOf(line.product), headroomFor(line));
  }

  function cancelQuote() {
    if (quoteTimer !== null) clearTimeout(quoteTimer);
    quoteTimer = null;
    quoteSeq++; // an answer still in flight is now stale
  }

  function stopPolling() {
    if (pollTimer !== null) clearTimeout(pollTimer);
    pollTimer = null;
    pollingId = null;
    polling.value = false;
  }

  /** Stops every timer (tests, and a new credential). */
  function stopTimers() {
    stopPolling();
    cancelQuote();
  }

  function report(err: EditApiError, action: string) {
    if (!isUnexpected(err)) return;
    // Status and code only: the request carried the credential.
    Sentry.captureMessage(`order-edit ${action} failed`, {
      level: 'error',
      tags: { feature: 'order-edit', action, status: String(err.status ?? 'none'), code: err.code },
    });
  }

  function resetDraft() {
    cancelQuote();
    targets.value = Object.fromEntries(lines.value.map((l) => [l.product, l.current]));
    quote.value = null;
    quoteState.value = 'idle';
    quoteError.value = null;
    quoteChanged.value = false;
    actionError.value = null;
  }

  function resetAll() {
    stopTimers();
    loadSeq++;
    view.value = null;
    loadState.value = 'idle';
    loadError.value = null;
    targets.value = {};
    quote.value = null;
    quoteState.value = 'idle';
    quoteError.value = null;
    quoteChanged.value = false;
    submitState.value = 'idle';
    actionError.value = null;
    deadlinePassed.value = false;
    outcome.value = null;
    linkState.value = 'idle';
    linkSentTo.value = null;
    linkError.value = null;
  }

  function markDeadlinePassed() {
    cancelQuote();
    deadlinePassed.value = true;
    quote.value = null;
    quoteState.value = 'idle';
  }

  function markGone(code: EditErrorCode) {
    cancelQuote();
    loadState.value = 'error';
    loadError.value = code;
    quote.value = null;
    quoteState.value = 'idle';
  }

  function setRuleError(err: EditApiError) {
    quote.value = null;
    quoteState.value = 'error';
    quoteError.value = { code: err.code, product: typeof err.body.product === 'string' ? err.body.product : null };
  }

  async function fetchView(cred: EditCredential, action: 'read' | 'reload'): Promise<void> {
    const seq = ++loadSeq;
    const res = await getEditView(cred);
    if (seq !== loadSeq) return;
    if (res.success && isEditView(res.data)) {
      view.value = res.data;
      loadState.value = 'ready';
      loadError.value = null;
      resetDraft();
      return;
    }
    const err: EditApiError = res.success
      ? { status: res.status ?? null, code: 'unknown', body: {} }
      : readEditError(res);
    report(err, action);
    if (err.code === 'deadline_passed') deadlinePassed.value = true;
    cancelQuote();
    view.value = null;
    loadState.value = 'error';
    loadError.value = err.code;
  }

  /**
   * Opens the page for one credential. Idempotent while that credential is
   * loading or loaded: the dish page mounts the block twice, then the summary
   * step mounts it again. The credential is recorded before the first await,
   * so startPaidPolling can follow right away.
   */
  async function load(cred: EditCredential): Promise<void> {
    if (sameCredential(credential, cred) && (loadState.value === 'loading' || loadState.value === 'ready')) return;
    if (!sameCredential(credential, cred)) resetAll();
    credential = cred;
    if (credentialValue(cred) === '') {
      loadState.value = 'error';
      loadError.value = 'invalid_link';
      return;
    }
    loadState.value = 'loading';
    await fetchView(cred, 'read');
  }

  /** Reads the order again (after an applied change); the outcome message stays. */
  async function reload(): Promise<void> {
    if (credential) await fetchView(credential, 'reload');
  }

  async function runQuote(): Promise<void> {
    const cred = credential;
    if (!cred) return;
    const seq = ++quoteSeq;
    const res = await quoteEdit(cred, changedTargets.value);
    if (seq !== quoteSeq) return;
    if (res.success && res.data) {
      quote.value = res.data;
      quoteState.value = 'ready';
      return;
    }
    const err = readEditError(res);
    report(err, 'quote');
    if (err.code === 'deadline_passed') return markDeadlinePassed();
    if (GONE.has(err.code)) return markGone(err.code);
    if (RULE_ERRORS.has(err.code)) return setRuleError(err);
    quoteState.value = 'error';
    actionError.value = err.code;
  }

  function scheduleQuote() {
    cancelQuote();
    quote.value = null;
    quoteError.value = null;
    quoteChanged.value = false;
    actionError.value = null;
    if (pendingChanges.value.length === 0 || mealsBelowHeadcount.value) {
      quoteState.value = 'idle';
      return;
    }
    quoteState.value = 'pending';
    quoteTimer = setTimeout(() => {
      quoteTimer = null;
      void runQuote();
    }, QUOTE_DEBOUNCE_MS);
  }

  function setTarget(product: string, wanted: number) {
    const line = lineOf(product);
    const v = view.value;
    if (!line || !v || !isLineEditable(line) || deadlinePassed.value || submitState.value !== 'idle') return;
    const current = targetOf(product);
    if (!unlockedOf(line) && wanted > current) return;
    const next = clampTarget(line, wanted, current, headroomFor(line));
    if (next === current) return;
    targets.value[product] = next;
    if (line.category === 'meal') {
      // Fewer menus: a line unlocked by the extra menus goes back to 0.
      for (const other of lines.value) {
        if (!unlockedOf(other) && targetOf(other.product) > other.current) targets.value[other.product] = other.current;
      }
    }
    if (isBrunch(line) && next > 0) {
      // One brunch product at most: picking one drops the other.
      for (const other of v.sections.options) {
        if (
          other.product !== product &&
          isBrunch(other) &&
          other.min === 0 &&
          isLineEditable(other) &&
          targetOf(other.product) > 0
        ) {
          targets.value[other.product] = 0;
        }
      }
    }
    if (!polling.value) outcome.value = null;
    scheduleQuote();
  }

  function addLine(product: string) {
    const line = lineOf(product);
    if (line && targetOf(product) === 0) setTarget(product, firstQuantity(line));
  }

  /** Submits the quoted change. Returns the payment page for the component to open, or null. */
  async function submit(): Promise<SubmitResult | null> {
    const cred = credential;
    const q = quote.value;
    if (!cred || !q || !canSubmit.value) return null;
    submitState.value = 'submitting';
    actionError.value = null;
    const res = await submitEdit(cred, changedTargets.value, q.netCents);
    if (res.success && res.data) {
      const result = res.data;
      if (result.status === 'payment_required') {
        // Stays 'redirecting': a second click would open a second payment.
        submitState.value = 'redirecting';
        return result;
      }
      submitState.value = 'idle';
      outcome.value = result.status === 'applied' ? 'applied' : 'processing';
      await reload();
      return result;
    }
    submitState.value = 'idle';
    const err = readEditError(res);
    report(err, 'submit');
    switch (err.code) {
      case 'quote_changed': {
        const fresh = err.body.quote as Quote | undefined;
        if (fresh && typeof fresh.netCents === 'number' && Array.isArray(fresh.lines)) {
          quote.value = fresh;
          quoteState.value = 'ready';
          quoteChanged.value = true;
        } else {
          scheduleQuote();
        }
        return null;
      }
      case 'payment_in_progress':
        outcome.value = 'payment_in_progress';
        await reload();
        return null;
      case 'modification_pending':
        outcome.value = 'modification_pending';
        return null;
      case 'deadline_passed':
        markDeadlinePassed();
        return null;
      default:
        if (GONE.has(err.code)) markGone(err.code);
        else if (RULE_ERRORS.has(err.code)) setRuleError(err);
        else actionError.value = err.code;
        return null;
    }
  }

  /**
   * Back from Stripe's success URL (`&paid=<id>`): asks until the
   * modification is applied, every intervalMs for up to timeoutMs. Works
   * whatever the read answered (the status route is never gated).
   */
  function startPaidPolling(id: number, opts: PollOptions = {}) {
    const cred = credential;
    if (!cred || pollingId === id) return;
    stopPolling();
    const intervalMs = opts.intervalMs ?? STATUS_POLL_INTERVAL_MS;
    const timeoutMs = opts.timeoutMs ?? STATUS_POLL_TIMEOUT_MS;
    pollingId = id;
    polling.value = true;
    outcome.value = null;
    let elapsed = 0;
    let last: ModificationStatus | null = null;
    const tick = async () => {
      pollTimer = null;
      const res = await getEditStatus(cred, id);
      if (pollingId !== id) return;
      if (res.success && res.data) last = res.data.status;
      if (last === 'applied') {
        polling.value = false;
        outcome.value = 'applied';
        void reload();
        return;
      }
      // pending_payment (the webhook has not landed), paid (being applied),
      // expired or needs_attention (§6.7 may still apply a paid session) or
      // no answer: Stripe sent the customer back here, so the money is in.
      if (elapsed + intervalMs > timeoutMs) {
        polling.value = false;
        outcome.value = last === 'needs_attention' ? 'needs_attention' : 'paid_pending';
        return;
      }
      elapsed += intervalMs;
      pollTimer = setTimeout(() => void tick(), intervalMs);
    };
    void tick();
  }

  /** E-mails the personal edit link (menu link only). */
  async function requestLink(): Promise<void> {
    const cred = credential;
    if (!cred || !('menuToken' in cred) || linkState.value === 'sending') return;
    linkState.value = 'sending';
    linkError.value = null;
    const res = await requestEditLink(cred.menuToken);
    if (res.success && res.data) {
      linkSentTo.value = res.data.sentTo;
      linkState.value = 'sent';
      return;
    }
    const err = readEditError(res);
    report(err, 'link');
    linkState.value = 'error';
    linkError.value = err.code;
    if (err.code === 'deadline_passed') markDeadlinePassed();
    else if (GONE.has(err.code)) markGone(err.code);
  }

  return {
    loadState,
    loadError,
    view,
    targets,
    quote,
    quoteState,
    quoteError,
    quoteChanged,
    submitState,
    actionError,
    deadlinePassed,
    outcome,
    polling,
    linkState,
    linkSentTo,
    linkError,
    lines,
    pendingChanges,
    changedTargets,
    mealsDelta,
    newHeadcount,
    mealsBelowHeadcount,
    capacityReached,
    canSubmit,
    lineOf,
    labelOf,
    targetOf,
    maxOf,
    unlockedOf,
    load,
    reload,
    setTarget,
    addLine,
    submit,
    startPaidPolling,
    stopPolling,
    stopTimers,
    requestLink,
  };
});
